/**
 * Live session state: one fetch, one socket, and an honest fallback.
 *
 * The socket is a notification channel and nothing else — it carries no rating
 * values, so losing it costs the table freshness, never data. Every write in
 * this app goes over HTTP or into the outbox before anyone is told about it.
 * When the socket dies this hook degrades to polling and SAYS SO, because a
 * screen that silently stops updating during a blind round is worse than one
 * that admits it is a few seconds behind.
 *
 * On reconnect the state is re-read in full rather than patched from events.
 * The redacted read model is the authority on what this device may see, and
 * replaying a missed `aspect_revealed` by hand would mean this client deciding
 * what is visible.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchSessionState, socketUrl, type SessionState } from "./api.ts";
import { localiseSnapshot, useLocale } from "../../locale.ts";

/** What the screen should tell the user about its own freshness. */
export type Channel = "connecting" | "live" | "polling" | "offline";

export interface LiveSession {
  state: SessionState | null;
  error: string | null;
  loading: boolean;
  channel: Channel;
  /** The last server event that arrived, for a subtle "Lea just answered" cue. */
  lastEventAt: number | null;
  /**
   * The host threw this night away while the screen was open.
   *
   * The one event this hook does not respond to by re-reading, because there is
   * nothing left to read: `loadSession` filters tombstones, so the re-read would
   * 404, set `error`, and leave `state` holding the table as it stood a second
   * ago. That is the "Live updates are down" failure wearing the wrong face — a
   * frozen screen the guest has no reason to distrust. So the fact is carried as
   * its own flag and the screens say it in words.
   */
  cancelled: boolean;
  refresh: () => Promise<void>;
}

const POLL_MS = 4_000;
const RECONNECT_MS = 3_000;

export interface UseSessionOptions {
  /** Whose turn it is on a shared device. Redacts the read model to them. */
  as?: string | null;
  /** A guest device's join grant. Absent means "the owner, by cookie". */
  token?: string | null;
  enabled?: boolean;
}

export function useSessionLive(
  sessionId: string,
  options: UseSessionOptions = {},
): LiveSession {
  const { as = null, token = null, enabled = true } = options;

  const [state, setState] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [channel, setChannel] = useState<Channel>("connecting");
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const [cancelled, setCancelled] = useState(false);

  // Refs, not state: the socket callbacks must read the current `as` without
  // being torn down and rebuilt every time the phone is passed to someone else.
  const asRef = useRef(as);
  asRef.current = as;
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const aliveRef = useRef(true);
  // The mirror of `cancelled` that `load` can read. Putting it in the callback's
  // deps instead would rebuild `load`, and the effect below re-runs on `load` —
  // so the socket would be torn down and reconnected to a session that is gone.
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    if (!enabled || !sessionId || cancelledRef.current) return;
    try {
      const next = await fetchSessionState(sessionId, {
        as: asRef.current,
        token: tokenRef.current,
      });
      if (!aliveRef.current) return;
      setState(next);
      setError(null);
    } catch (err) {
      if (!aliveRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
      // A failed read is the only thing that proves we are actually cut off.
      setChannel(navigator.onLine === false ? "offline" : "polling");
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [enabled, sessionId]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // A different night has its own fate. Without this the flag would outlive the
  // session it describes and the next screen would open already dead.
  useEffect(() => {
    cancelledRef.current = false;
    setCancelled(false);
  }, [sessionId]);

  // Re-read whenever the acting participant changes: the server redacts to the
  // actor, so the phone changing hands changes what may legitimately be shown.
  useEffect(() => {
    if (!enabled || cancelled) return;
    setLoading(true);
    void load();
  }, [enabled, load, as, cancelled]);

  useEffect(() => {
    if (!enabled || !sessionId || cancelled) return;

    let socket: WebSocket | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let reconnect: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const startPolling = () => {
      if (poll) return;
      poll = setInterval(() => void load(), POLL_MS);
    };
    const stopPolling = () => {
      if (!poll) return;
      clearInterval(poll);
      poll = null;
    };

    const connect = () => {
      if (closed) return;
      let ws: WebSocket;
      try {
        ws = new WebSocket(socketUrl({ sessionId, token: tokenRef.current }));
      } catch {
        setChannel("polling");
        startPolling();
        return;
      }
      socket = ws;

      ws.onopen = () => {
        if (closed) return;
        setChannel("live");
        stopPolling();
        // The socket may have been down across a reveal; re-read rather than
        // assume nothing happened while we were not listening.
        void load();
      };

      ws.onmessage = (event) => {
        if (closed || typeof event.data !== "string") return;
        try {
          const parsed = JSON.parse(event.data) as { type?: string };
          if (!parsed.type) return;
          setLastEventAt(Date.now());
          if (parsed.type === "session_cancelled") {
            // The one frame that is itself the news. The ref goes first so an
            // in-flight `load` cannot land a 404 on top of the flag; the effect
            // then re-runs and its cleanup closes this socket and the poll.
            cancelledRef.current = true;
            setCancelled(true);
            setLoading(false);
            return;
          }
          // Every other event means "the server knows something you do not".
          // None of them carry a value, so the only correct response is to
          // re-read.
          void load();
        } catch {
          // A frame we cannot parse is not a reason to drop the connection.
        }
      };

      const degrade = () => {
        if (closed) return;
        setChannel(navigator.onLine === false ? "offline" : "polling");
        startPolling();
        if (reconnect) clearTimeout(reconnect);
        reconnect = setTimeout(connect, RECONNECT_MS);
      };

      ws.onclose = degrade;
      ws.onerror = degrade;
    };

    connect();

    const onOnline = () => {
      setChannel("connecting");
      void load();
      connect();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      closed = true;
      stopPolling();
      if (reconnect) clearTimeout(reconnect);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      if (socket) {
        const dropped = socket;
        dropped.onclose = null;
        dropped.onerror = null;
        dropped.onmessage = null;
        if (dropped.readyState === WebSocket.CONNECTING) {
          // Closing mid-handshake logs "WebSocket is closed before the
          // connection is established" — leaving the session screen quickly
          // enough put that in the console every time. Let the handshake finish
          // and drop it then; the socket is already unwired, so anything that
          // arrives in between goes nowhere.
          dropped.onopen = () => dropped.close();
        } else {
          dropped.close();
        }
      }
    };
  }, [enabled, sessionId, load, cancelled]);

  // The language is applied here rather than in `load`, so that switching it in
  // settings repaints an open night instead of waiting for the next poll. The
  // fetched object is left untouched: `state` stays what the server said, and
  // every screen reads the translated copy derived from it.
  const locale = useLocale();
  const shown = useMemo<SessionState | null>(() => {
    if (!state) return null;
    return {
      ...state,
      aspects: state.aspects.map((view) => ({
        ...view,
        aspect: localiseSnapshot(view.aspect, locale),
      })),
      subquestions: (state.subquestions ?? []).map((view) => ({
        ...view,
        subquestion: localiseSnapshot(view.subquestion, locale),
      })),
    };
  }, [state, locale]);

  return { state: shown, error, loading, channel, lastEventAt, cancelled, refresh: load };
}

/**
 * One line of plain English about the transport, or null when it is fine.
 *
 * `finished` is the session's own status, not the socket's. A night that is
 * over has no live table to fall behind and nothing of this device's queued to
 * send, so every transport sentence below is false of it — worst of all the
 * offline one, which reads as "your ratings are stuck here". What is actually
 * true is narrower: the screen is a record coming back off the cache, and that
 * is worth saying only when the network is gone and the copy may be stale.
 */
export function channelNotice(channel: Channel, finished = false): string | null {
  if (finished) {
    return channel === "offline"
      ? "Offline. This night is finished — showing the last copy this device downloaded."
      : null;
  }
  switch (channel) {
    case "live":
      return null;
    case "connecting":
      return "Connecting to the table…";
    case "polling":
      return "Live updates are down — checking every few seconds.";
    case "offline":
      return "Offline. Ratings are saved on this device and sent when you are back.";
  }
}
